import { readPath } from "~/helpers/offline/idempotencyKey";
import buildQueueDetail, { type DetailToken } from "~/helpers/offline/queueDetail";
import { OFFLINE_QUEUE_APP, type OfflineQueueApp, type QueueEntryMeta } from "~/helpers/offline/queueMeta";

type GraphQLOperationName = string;

type NamespacedI18nKey = string;

interface QueueMetaDefinition {
	app: OfflineQueueApp;
	detailTokens?: DetailToken[];
	labelKey: NamespacedI18nKey;
	reconciliationIdField?: string;
}

export const readInputField = (variables: unknown, fieldPath: string): string | undefined => {
	if (!variables || typeof variables !== "object") return undefined;
	const { input } = variables as { input?: unknown };
	const value = readPath(input ?? variables, fieldPath);
	return typeof value === "string" && value.length > 0 ? value : undefined;
};

const materialItemAtLocationTokens = (itemPath: string, locationPath: string, amountPath: string): DetailToken[] => [
	{ kind: "id", path: itemPath },
	{ kind: "id", path: locationPath },
	{ kind: "amount", path: amountPath }
];

const materialMoveTokens = (
	itemPath: string,
	amountPath: string,
	fromPaths: string[],
	toPaths: string[]
): DetailToken[] => [
	{ kind: "id", path: itemPath },
	{ kind: "amount", path: amountPath },
	{ kind: "route", fromPaths, toPaths }
];

export const QUEUE_META_REGISTRY: Record<GraphQLOperationName, QueueMetaDefinition> = {
	AddTaskAssignmentNote: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.addTaskNote"
	},
	AdjustInventoryItem: {
		app: OFFLINE_QUEUE_APP.AVAILABILITY,
		detailTokens: materialItemAtLocationTokens("itemId", "locationId", "quantity"),
		labelKey: "mobile:availability.queue.adjustInventory",
		reconciliationIdField: "idempotencyKey"
	},
	AdjustMaterialItem: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: materialItemAtLocationTokens("materialItemId", "locationId", "quantity"),
		labelKey: "mobile:material.queue.adjust"
	},
	AtomicMove: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: materialMoveTokens(
			"lines.0.materialItemId",
			"lines.0.quantity",
			["fromLocationId", "fromSiteLocationId"],
			["toLocationId", "toSiteLocationId"]
		),
		labelKey: "mobile:material.queue.move",
		reconciliationIdField: "idempotentKey"
	},
	AttachAvailabilitySessionPhoto: {
		app: OFFLINE_QUEUE_APP.AVAILABILITY,
		detailTokens: [
			{ kind: "id", path: "itemId" },
			{ kind: "id", path: "locationId" }
		],
		labelKey: "mobile:availability.queue.attachPhoto",
		reconciliationIdField: "idempotencyKey"
	},
	CancelTask: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.cancelTask"
	},
	CompleteAssignmentTask: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: [
			{ kind: "id", path: "assignmentId" },
			{ kind: "id", path: "materialItemId" },
			{ kind: "id", path: "binLocationId" },
			{ kind: "amount", path: "completedQty" }
		],
		labelKey: "mobile:material.queue.countComplete",
		reconciliationIdField: "idempotentKey"
	},
	CompleteTask: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.completeTask"
	},
	CountMaterialItem: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: materialItemAtLocationTokens("materialItemId", "locationId", "quantityCounted"),
		labelKey: "mobile:material.queue.count",
		reconciliationIdField: "idempotencyKey"
	},
	CreateMovementOrder: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: materialMoveTokens(
			"lines.0.materialItemId",
			"lines.0.quantity",
			["fromLocationId", "fromSiteLocationId"],
			["toLocationId", "toSiteLocationId"]
		),
		labelKey: "mobile:material.queue.transferCreate",
		reconciliationIdField: "idempotentKey"
	},
	CreateTaskAssignment: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.createTaskAssignment"
	},
	DiscardFloatingTimeEntry: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.discardTimeEntry",
		reconciliationIdField: "floatingTimeEntryId"
	},
	GradeInventoryItem: {
		app: OFFLINE_QUEUE_APP.AVAILABILITY,
		detailTokens: materialItemAtLocationTokens("itemId", "locationId", "quantity"),
		labelKey: "mobile:availability.queue.gradeInventory",
		reconciliationIdField: "idempotencyKey"
	},
	IncrementInventoryItem: {
		app: OFFLINE_QUEUE_APP.AVAILABILITY,
		detailTokens: materialItemAtLocationTokens("itemId", "locationId", "quantity"),
		labelKey: "mobile:availability.queue.incrementInventory",
		reconciliationIdField: "idempotencyKey"
	},
	LinkFloatingTimeEntry: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		detailTokens: [{ kind: "amount", path: "allocations" }],
		labelKey: "mobile:events.queue.linkTimeEntry",
		reconciliationIdField: "floatingTimeEntryId"
	},
	MoveInventoryItem: {
		app: OFFLINE_QUEUE_APP.AVAILABILITY,
		detailTokens: materialMoveTokens("itemId", "quantity", ["locationId"], ["toLocationId"]),
		labelKey: "mobile:availability.queue.moveInventory",
		reconciliationIdField: "idempotencyKey"
	},
	MoveMaterialItem: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: materialMoveTokens("materialItemId", "quantityMoved", ["fromLocationId"], ["toLocationId"]),
		labelKey: "mobile:material.queue.move",
		reconciliationIdField: "idempotencyKey"
	},
	PostCycleCounts: {
		app: OFFLINE_QUEUE_APP.ENGAGE,
		detailTokens: [{ kind: "amount", path: "items" }],
		labelKey: "mobile:engage.queue.postCounts",
		reconciliationIdField: "idempotencyKey"
	},
	ReassignTask: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.reassignTask"
	},
	ReceiveMovementOrder: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: [{ kind: "id", path: "movementOrderId" }],
		labelKey: "mobile:material.queue.transferReceive",
		reconciliationIdField: "idempotentKey"
	},
	ReceiveVendorPurchaseOrderItems: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: [
			{ kind: "id", path: "vendorPurchaseOrderId" },
			{ kind: "amount", path: "items" }
		],
		labelKey: "mobile:material.queue.receive",
		reconciliationIdField: "idempotencyKey"
	},
	RecordManualJobCost: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		detailTokens: [{ kind: "amount", path: "amount" }],
		labelKey: "mobile:events.queue.recordJobCost",
		reconciliationIdField: "recordId"
	},
	RecordOrderPulls: {
		app: OFFLINE_QUEUE_APP.VERIFY,
		detailTokens: [{ kind: "amount", path: "pulls" }],
		labelKey: "mobile:verify.queue.pull",
		reconciliationIdField: "pullAttemptKey"
	},
	RecordShrinkEvent: {
		app: OFFLINE_QUEUE_APP.SHRINK,
		detailTokens: [
			{ kind: "id", path: "input.shrinkEventId" },
			{ kind: "amount", path: "input.quantity" }
		],
		labelKey: "mobile:shrink.queue.record",
		reconciliationIdField: "input.shrinkEventId"
	},
	RemoveShrinkEvent: {
		app: OFFLINE_QUEUE_APP.SHRINK,
		detailTokens: [{ kind: "id", path: "shrinkEventId" }],
		labelKey: "mobile:shrink.queue.remove",
		reconciliationIdField: "shrinkEventId"
	},
	ScrapInventoryItem: {
		app: OFFLINE_QUEUE_APP.AVAILABILITY,
		detailTokens: materialItemAtLocationTokens("itemId", "locationId", "quantity"),
		labelKey: "mobile:availability.queue.scrapInventory",
		reconciliationIdField: "idempotencyKey"
	},
	ShipMovementOrder: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: [
			{ kind: "id", path: "movementOrderId" },
			{ kind: "id", path: "shippedLines.0.materialItemId" },
			{ kind: "amount", path: "shippedLines.0.quantity" }
		],
		labelKey: "mobile:material.queue.transferShip",
		reconciliationIdField: "idempotentKey"
	},
	SkipAssignmentTask: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: [
			{ kind: "id", path: "assignmentId" },
			{ kind: "id", path: "materialItemId" },
			{ kind: "id", path: "binLocationId" }
		],
		labelKey: "mobile:material.queue.countSkip",
		reconciliationIdField: "idempotentKey"
	},
	StartAssignmentTask: {
		app: OFFLINE_QUEUE_APP.MATERIAL,
		detailTokens: [
			{ kind: "id", path: "assignmentId" },
			{ kind: "id", path: "materialItemId" },
			{ kind: "id", path: "binLocationId" }
		],
		labelKey: "mobile:material.queue.countStart",
		reconciliationIdField: "idempotentKey"
	},
	StartFloatingTimer: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.startTimer",
		reconciliationIdField: "floatingTimeEntryId"
	},
	StartTask: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.startTask"
	},
	StartTaskAssignmentTimer: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.startTaskTimer"
	},
	StopFloatingTimer: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.stopTimer",
		reconciliationIdField: "floatingTimeEntryId"
	},
	StopTaskAssignmentTimer: {
		app: OFFLINE_QUEUE_APP.EVENTS,
		labelKey: "mobile:events.queue.stopTaskTimer"
	},
	SubmitVerifyContainer: {
		app: OFFLINE_QUEUE_APP.VERIFY,
		labelKey: "mobile:verify.queue.container",
		reconciliationIdField: "containerId"
	},
	UpsertRfidScans: {
		app: OFFLINE_QUEUE_APP.ENGAGE,
		detailTokens: [{ kind: "amount", path: "length" }],
		labelKey: "mobile:engage.queue.postRfidScans",
		reconciliationIdField: "0.serialId"
	}
};

const resolveQueueMeta = (
	operationName: string,
	variables: unknown,
	enqueuedAt: number
): QueueEntryMeta | undefined => {
	const definition = QUEUE_META_REGISTRY[operationName];
	if (!definition) return undefined;
	const reconciliationId = definition.reconciliationIdField
		? readInputField(variables, definition.reconciliationIdField)
		: undefined;
	const detail = buildQueueDetail(variables, definition.detailTokens);
	return {
		app: definition.app,
		enqueuedAt,
		labelKey: definition.labelKey,
		...(reconciliationId !== undefined ? { reconciliationId } : {}),
		...(detail !== undefined ? { detail } : {})
	};
};

export default resolveQueueMeta;
